// zoom-level-display.component.ts
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  input,
  NgZone,
  OnDestroy,
  OnInit
} from '@angular/core';

import * as reactiveUtils from '@arcgis/core/core/reactiveUtils';
import type { ResourceHandle } from '@arcgis/core/core/Handles';
import type MapView from '@arcgis/core/views/MapView';
import type SceneView from '@arcgis/core/views/SceneView';
import { JmkwApiService } from '@jeppesen-foreflight/jmkw-core';
import { EsriViewType } from '@jeppesen-foreflight/jmkw-types';

@Component({
  selector: 'app-zoom-level-display',
  standalone: true,
  imports: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `{{ zoomLevel }}`,
  // Theme-dependent styling (background, color, border, box-shadow) is
  // applied globally via ThemeService.buildCalciteOverrideCss() so cascade
  // ordering against Esri's main.css is guaranteed. Only layout and typography
  // belong here.
  styles: [
    `
      :host {
        display: flex;
        height: 32px;
        width: 32px;
        align-items: center;
        justify-content: center;
        font-size: 13px;
        font-weight: 700;
        cursor: default;
        user-select: none;
      }
    `
  ]
})
export class ZoomLevelDisplayComponent implements OnInit, OnDestroy {
  private readonly _apiService = inject(JmkwApiService);
  private readonly _cdr = inject(ChangeDetectorRef);
  private readonly _ngZone = inject(NgZone);

  // @arcgis/core v5 re-parents the DOM node passed to view.ui.add() into the
  // view's UI container, so a single instance cannot be shared between
  // MapView and SceneView.
  readonly viewType = input<EsriViewType>('2D');

  zoomLevel = '';
  private watchHandle?: ResourceHandle;

  ngOnInit(): void {
    const view: MapView | SceneView | undefined =
      this.viewType() === '2D'
        ? this._apiService.getMapView()
        : this._apiService.getSceneView();

    // JmkwApiService.getMapView()/getSceneView() read from store.views and
    // cast — the value is undefined before ViewService.init() runs. The
    // plugin uses a setTimeout to defer add() until views exist, but guard
    // here so any other caller without that timing assumption fails safely.
    if (!view) return;

    const initial = this.formatZoom(view.zoom);
    if (initial !== null) {
      this.zoomLevel = initial;
    }

    this.watchHandle = reactiveUtils.watch(
      () => view.zoom,
      (zoom) => {
        const formatted = this.formatZoom(zoom);
        // Filter transient invalid values (-1, NaN) emitted during basemap /
        // view-construction transitions. Keeping the last valid value visible
        // is better UX than briefly flashing "-1" or going blank.
        if (formatted === null) return;
        this._ngZone.run(() => {
          this.zoomLevel = formatted;
          this._cdr.markForCheck();
        });
      }
    );
  }

  ngOnDestroy(): void {
    this.watchHandle?.remove();
  }

  /**
   * Returns the floored zoom as a string, or `null` if the value is not a
   * valid zoom level. ArcGIS views report `zoom = -1` while a view is not
   * ready (e.g. during basemap construction); we suppress those readings
   * so they do not surface in the template.
   */
  private formatZoom(zoom: number): string | null {
    return zoom >= 0 && Number.isFinite(zoom)
      ? Math.floor(zoom).toString()
      : null;
  }
}
